import { useRouter } from 'next/router';
import type { Filters } from '@/domain';
import { parseUrl } from '@/scenes/resultPage/helpers';

export const SmallFhhMap = ({ filters }: { filters: Filters }) => {
    const router = useRouter();
    const searchParams = parseUrl(router.query);

    const midpointParams = (latSw: string, lngSw: string, latNe: string, lngNe: string) =>
        `lat=${(Number.parseFloat(latSw) + Number.parseFloat(latNe)) / 2}&lng=${(Number.parseFloat(lngSw) + Number.parseFloat(lngNe)) / 2}`;

    const zoomLevel = calculateZoomLevel(filters);
    const mapCoords =
        filters.lng_sw && filters.lat_sw && filters.lng_ne && filters.lat_ne
            ? midpointParams(filters.lat_sw, filters.lng_sw, filters.lat_ne, filters.lng_ne)
            : 'lat=59.925668&lng=10.762596'; // Oslo
    const imageUrl = `https://maptiles.finncdn.no/staticmap?${mapCoords}&zoom=${zoomLevel}&maptype=norwayVector&showPin=false&size=590x194`;

    return (
        <div className="relative mb-8 mini-map map-image-button" data-testid="miniMap">
            <a href={`${router.basePath}/kart/?${searchParams.toString()}`}>
                <img src={imageUrl} alt="Vis i kart" className="w-full clickable rounded-8" />
            </a>
        </div>
    );
};

const calculateZoomLevel = (filters: Filters, mapWidth = 590, mapHeight = 194): number => {
    if (!filters.lat_sw || !filters.lng_sw || !filters.lat_ne || !filters.lng_ne) {
        //Fallback to previous logic if we dont have the complete bounding box
        return filters.country ? 3 : 6;
    }

    // Find the span of lat and long
    const lngSpan = Math.abs(parseFloat(filters.lng_ne) - parseFloat(filters.lng_sw));
    const latSpan = Math.abs(parseFloat(filters.lat_ne) - parseFloat(filters.lat_sw));

    // At zoom 0, web-based map representations display the full world (360° lng, 180° lat) in 256 x 256 pixels:
    // https://developers.google.com/maps/documentation/javascript/coordinates
    const DEFAULT_MAP_SIZE = 256;

    // 360 / lngSpan: How many times does the longitude span fit into the full 360° world?
    // mapWidth / DEFAULT_MAP_SIZE: How many times does the default map size fit into our image width?
    // The log base 2 of their product gives us the zoom level that would fill our map's width with the longitude span
    // The reason is that each zoom level doubles the amount of pixels needed to show the whole world.
    const zoomByLng = Math.log2((mapWidth / DEFAULT_MAP_SIZE) * (360 / lngSpan));

    // repeat for lat
    const zoomByLat = Math.log2((mapHeight / DEFAULT_MAP_SIZE) * (180 / latSpan));

    // Take the smaller value (less zoomed in) to ensure the entire bounding box fits within the image
    const zoom = Math.floor(Math.min(zoomByLng, zoomByLat));

    // Restrict between 2 and 15 so we never get absurdly far out or absurdly close
    return Math.max(2, Math.min(zoom, 15));
};
